import {addField, removeElement, updateElement, FIELD, initialFieldValues} from "./builder.js";
import {resolveDependencyMismatches} from "../pages/checksheet-builder/helpers.js";

/**
 * Persisted field properties used when diffing template field definitions.
 * Based on {@link initialFieldValues} (FieldBuilder) plus formula/elapsed/conditional extras.
 */
const FIELD_COMPARE_EXTRA_KEYS = [
  "trueFormula",
  "condition",
  "help",
  "hasInfinity",
  "hasPrecision",
  "absoluteValue",
  "hidden",
  "hasDivideByZeroDefault",
  "divideByZeroDefault",
  "minutes",
  "phone"
];

const FIELD_COMPARE_KEYS = [
  ...new Set([...Object.keys(initialFieldValues), ...FIELD_COMPARE_EXTRA_KEYS])
];

/**
 * @param {string} label
 * @returns {string}
 */
export const normalizeLabel = label => (label || "").replace(/\s+/g, " ").trim().toUpperCase();

/**
 * @param {object} instanceField
 * @param {object} templateField
 * @returns {boolean}
 */
export const matchInstanceFieldToTemplate = (instanceField, templateField) => {
  if (!instanceField || !templateField) return false;
  if (instanceField.shared) return false;
  return (
    normalizeLabel(instanceField.label) === normalizeLabel(templateField.label) &&
    instanceField.type === templateField.type
  );
};

/**
 * @param {object} field
 * @returns {string}
 */
const fieldSignature = field => {
  const picked = {};
  FIELD_COMPARE_KEYS.forEach(key => {
    if (field[key] !== undefined) picked[key] = field[key];
  });
  return JSON.stringify(picked);
};

/**
 * @param {Array.<object>} previousFields
 * @param {Array.<object>} newFields
 * @returns {{hasChanges: boolean, added: string[], removed: string[], updated: string[]}}
 */
export const diffTemplateFields = (previousFields = [], newFields = []) => {
  const prevByName = Object.fromEntries(previousFields.filter(f => f?.name).map(f => [f.name, f]));
  const newByName = Object.fromEntries(newFields.filter(f => f?.name).map(f => [f.name, f]));

  const added = Object.keys(newByName).filter(name => !(name in prevByName));
  const removed = Object.keys(prevByName).filter(name => !(name in newByName));
  const updated = Object.keys(newByName).filter(
    name =>
      name in prevByName && fieldSignature(prevByName[name]) !== fieldSignature(newByName[name])
  );

  return {
    hasChanges: added.length > 0 || removed.length > 0 || updated.length > 0,
    added,
    removed,
    updated
  };
};

/**
 * @param {object} template
 * @returns {boolean}
 */
export const hasTemplateInstances = template => {
  if (!template?.instances) return false;
  return Object.values(template.instances).some(names => Array.isArray(names) && names.length > 0);
};

/**
 * @param {object} templateField
 * @returns {object}
 */
const propsFromTemplateField = templateField => {
  const copy = {...templateField};
  delete copy.name;
  delete copy.element;
  delete copy.parentName;
  delete copy.children;
  delete copy.toggle;
  delete copy.generates;
  if (copy.units && typeof copy.units === "string") {
    copy.units = copy.units.replace("Other: ", "");
  }
  return copy;
};

/**
 * @param {object} node
 * @returns {boolean}
 */
const isChecksheetComponent = node => {
  if (!node) return false;
  if (node.element === "component") return true;
  if (node.element === "group" || node.element === "field" || node.type) return false;
  return Array.isArray(node.children) || node.parentName !== undefined;
};

/**
 * @param {object} builder
 * @param {object} [facilityById]
 * @param {string} componentName
 * @returns {{node: object, label: string}|null}
 */
const resolveChecksheetComponent = (builder, facilityById, componentName) => {
  const node = builder?.byId?.[componentName];
  if (!isChecksheetComponent(node)) return null;
  const label = node.label || facilityById?.[componentName]?.label || componentName;
  return {node, label};
};

/**
 * Find a single instance child field for a template field definition.
 * Tries label+type, help keys, template field name as child id, then field order.
 *
 * @param {object} builder
 * @param {string} componentName
 * @param {object} templateField
 * @param {object} options
 * @returns {string|null}
 */
const findInstanceFieldForTemplate = (
  builder,
  componentName,
  templateField,
  excludeNames = new Set(),
  prevField = null
) => {
  if (!templateField) return null;

  const component = builder.byId[componentName];
  if (!component?.children) return null;

  const labelTypeMatches = fieldDef => {
    if (!fieldDef) return [];
    return component.children.filter(childName => {
      if (excludeNames.has(childName)) return false;
      const child = builder.byId[childName];
      return child?.element === FIELD && matchInstanceFieldToTemplate(child, fieldDef);
    });
  };

  const labelRenamed =
    prevField && normalizeLabel(prevField.label) !== normalizeLabel(templateField.label);

  // When the label was renamed, only match by the old label — the instance still has it.
  const matchDef = labelRenamed ? prevField : templateField;
  const matches = labelTypeMatches(matchDef);
  return matches.length === 1 ? matches[0] : null;
};

/**
 * @param {Array.<object>} help
 * @param {Array.<string>} children
 * @param {object} byId
 * @returns {Array.<object>|undefined}
 */
const syncComponentHelp = (help, children, byId) => {
  if (!help?.length) return undefined;
  return help
    .map(({key, value}) => {
      const childName = children.find(
        name =>
          byId[name]?.element === FIELD && normalizeLabel(byId[name].label) === normalizeLabel(key)
      );
      if (!childName) return null;
      return {key: byId[childName].label, value, id: childName};
    })
    .filter(Boolean);
};

/**
 * @param {object} previousTemplate
 * @param {object} newTemplate
 * @returns {boolean}
 */
const templateHelpChanged = (previousTemplate, newTemplate) => {
  const prevHelp = previousTemplate?.options?.help || [];
  const newHelp = newTemplate?.options?.help || [];
  return JSON.stringify(prevHelp) !== JSON.stringify(newHelp);
};

/**
 * Whether a saved template differs from the snapshot taken when edit mode started.
 *
 * @param {object} previousTemplate
 * @param {object} newTemplate
 * @returns {boolean}
 */
export const templateDefinitionChanged = (previousTemplate, newTemplate) =>
  diffTemplateFields(previousTemplate?.fields || [], newTemplate?.fields || []).hasChanges ||
  templateHelpChanged(previousTemplate, newTemplate);

/**
 * Merge template field changes into a checksheet component without removing
 * instance-only fields. Uses label + type matching.
 *
 * @param {object} builder
 * @param {string} componentName
 * @param {{previousTemplate: object, newTemplate: object}} templates
 * @returns {{builder: object, nameMap: object, changes: object, warnings: string[]}}
 */
export const mergeTemplateIntoComponent = (
  builder,
  componentName,
  {previousTemplate, newTemplate, facilityById = null}
) => {
  const warnings = [];
  const changes = {added: [], updated: [], removed: [], kept: []};
  const nameMap = {};

  const resolved = resolveChecksheetComponent(builder, facilityById, componentName);
  if (!resolved) {
    return {builder, nameMap, changes, warnings: ["Component not found in builder"]};
  }

  const {label: componentLabel} = resolved;

  const previousFields = previousTemplate?.fields || [];
  const newFields = newTemplate?.fields || [];
  const diff = diffTemplateFields(previousFields, newFields);

  if (!diff.hasChanges && !templateHelpChanged(previousTemplate, newTemplate)) {
    return {builder, nameMap, changes, warnings};
  }

  let working = {...builder, byId: {...builder.byId}, allIds: [...builder.allIds]};
  const usedInstanceNames = new Set();

  const prevByName = Object.fromEntries(previousFields.filter(f => f?.name).map(f => [f.name, f]));
  const newByName = Object.fromEntries(newFields.filter(f => f?.name).map(f => [f.name, f]));

  // Removals: template field name removed — remove matching instance field only
  diff.removed.forEach(templateFieldName => {
    const prevField = prevByName[templateFieldName];
    const instanceName = findInstanceFieldForTemplate(
      working,
      componentName,
      prevField,
      usedInstanceNames
    );
    if (!instanceName) {
      warnings.push(
        `Could not remove "${
          prevField?.label || templateFieldName
        }": no unique matching field on component "${componentLabel}".`
      );
      return;
    }
    usedInstanceNames.add(instanceName);
    working = removeElement(working, {name: instanceName, element: FIELD});
    changes.removed.push(instanceName);
  });

  // Updates: same template field name, definition changed
  diff.updated.forEach(templateFieldName => {
    const newField = newByName[templateFieldName];
    const prevField = prevByName[templateFieldName];
    const instanceName = findInstanceFieldForTemplate(
      working,
      componentName,
      newField,
      usedInstanceNames,
      prevField
    );
    if (!instanceName) {
      warnings.push(
        `Could not update "${
          newField?.label || templateFieldName
        }": no unique matching field on component "${componentLabel}".`
      );
      return;
    }
    usedInstanceNames.add(instanceName);
    const instanceField = {
      ...working.byId[instanceName],
      children: working.byId[instanceName].children || []
    };
    working = {
      ...working,
      byId: {...working.byId, [instanceName]: instanceField}
    };
    const templateProps = propsFromTemplateField(newField);
    const helpEntry = newTemplate?.options?.help?.find(
      ({key}) => normalizeLabel(key) === normalizeLabel(newField.label)
    );

    const updatePayload = {
      ...instanceField,
      ...templateProps,
      element: FIELD,
      name: instanceName,
      parentName: componentName,
      children: instanceField.children || [],
      toggle: instanceField.toggle ?? true,
      help: helpEntry?.value
    };

    ({builder: working} = updateElement(working, updatePayload, false, nameMap));
    nameMap[templateFieldName] = instanceName;
    changes.updated.push(instanceName);
  });

  // Additions: new template field names
  diff.added.forEach(templateFieldName => {
    const newField = newByName[templateFieldName];
    const existing = findInstanceFieldForTemplate(
      working,
      componentName,
      newField,
      usedInstanceNames
    );
    if (existing) {
      usedInstanceNames.add(existing);
      nameMap[templateFieldName] = existing;
      changes.kept.push(existing);
      return;
    }

    const helpEntry = newTemplate?.options?.help?.find(
      ({key}) => normalizeLabel(key) === normalizeLabel(newField.label)
    );

    working = addField(
      working,
      {
        ...propsFromTemplateField(newField),
        element: FIELD,
        parentName: componentName,
        label: newField.label,
        toggle: true,
        help: helpEntry?.value
      },
      nameMap,
      templateFieldName
    );
    changes.added.push(nameMap[templateFieldName]);
    if (nameMap[templateFieldName]) usedInstanceNames.add(nameMap[templateFieldName]);
  });

  // Sync component-level help when template help changed
  if (templateHelpChanged(previousTemplate, newTemplate) && newTemplate?.options?.help) {
    const comp = working.byId[componentName];
    const syncedHelp = syncComponentHelp(newTemplate.options.help, comp.children, working.byId);
    if (syncedHelp?.length) {
      ({builder: working} = updateElement(working, {
        ...comp,
        help: syncedHelp,
        hasHelp: true
      }));
    }
  }

  if (Object.keys(nameMap).length > 0) {
    resolveDependencyMismatches(working, nameMap);
  }

  return {builder: working, nameMap, changes, warnings};
};

/**
 * Build a template snapshot from API component shape (for diffing before/after save).
 *
 * @param {object} component
 * @returns {{fields: Array, options: object}}
 */
export const snapshotFromComponent = component => ({
  fields: (component?.fields || []).map(f => ({...f})),
  options: {
    help: component?.help || component?.options?.help,
    external: component?.external || component?.options?.external
  }
});

/**
 * Build a template snapshot from buildTemplate() output on the templates page.
 *
 * @param {object} built
 * @returns {{fields: Array, options: object}}
 */
export const snapshotFromBuiltTemplate = built => ({
  fields: (built?.fields || []).map(f => ({...f})),
  options: built?.options || {}
});

/**
 * @param {object} builder
 * @returns {object}
 */
const cloneBuilder = builder => {
  const byId = Object.fromEntries(
    Object.entries(builder?.byId || {}).map(([key, node]) => {
      const copy = {...node};
      if (Array.isArray(node.children)) copy.children = [...node.children];
      if (Array.isArray(node.generates)) copy.generates = [...node.generates];
      if (Array.isArray(node.depends)) copy.depends = [...node.depends];
      return [key, copy];
    })
  );
  return {byId, allIds: [...(builder?.allIds || [])]};
};

/**
 * Detect instance rows where label+type matching would be ambiguous on the component.
 *
 * @param {object} builder
 * @param {string} componentName
 * @param {Array.<object>} templateFields
 * @returns {boolean}
 */
export const hasAmbiguousFieldMatches = (builder, componentName, templateFields = []) => {
  const component = builder?.byId?.[componentName];
  if (!component?.children) return false;

  return templateFields.some(templateField => {
    const byLabelType = component.children.filter(childName => {
      const child = builder.byId[childName];
      return child?.element === FIELD && matchInstanceFieldToTemplate(child, templateField);
    });
    if (byLabelType.length > 1) return true;

    if (!templateField?.label || !component.help?.length) return false;
    const byHelp = component.help.filter(
      ({key}) => normalizeLabel(key) === normalizeLabel(templateField.label)
    );
    return byHelp.length > 1;
  });
};

/**
 * Dry-run the same merge logic used on save to decide if a row should appear in the modal.
 *
 * @param {object} builder
 * @param {string} componentName
 * @param {{previousTemplate: object, newTemplate: object, facilityById: object|null}} templates
 * @returns {{eligible: boolean, blockers: string[]}}
 */
export const assessPropagationEligibility = (
  builder,
  componentName,
  {previousTemplate, newTemplate, facilityById = null}
) => {
  if (!previousTemplate || !newTemplate) {
    return {eligible: true, blockers: []};
  }

  const resolved = resolveChecksheetComponent(builder, facilityById, componentName);
  if (!resolved) {
    return {eligible: false, blockers: ["Component not found on checksheet"]};
  }

  const previousFields = previousTemplate?.fields || [];
  const newFields = newTemplate?.fields || [];
  const diff = diffTemplateFields(previousFields, newFields);
  const helpOnly = !diff.hasChanges && templateHelpChanged(previousTemplate, newTemplate);

  if (!diff.hasChanges && !helpOnly) {
    return {eligible: false, blockers: ["No template changes apply to this component"]};
  }

  const fieldsToCheck = newFields.length ? newFields : previousFields;
  if (hasAmbiguousFieldMatches(builder, componentName, fieldsToCheck)) {
    return {
      eligible: false,
      blockers: ["Duplicate fields with the same label and type on this component"]
    };
  }

  const {warnings, changes} = mergeTemplateIntoComponent(cloneBuilder(builder), componentName, {
    previousTemplate,
    newTemplate,
    facilityById
  });

  if (warnings.some(w => /Component not found in builder/i.test(w))) {
    return {eligible: false, blockers: warnings};
  }

  const fieldWarnings = warnings.filter(w => /Could not (update|remove)/i.test(w));
  if (fieldWarnings.length) {
    return {eligible: false, blockers: fieldWarnings};
  }

  if (diff.hasChanges) {
    const applied = changes.added.length + changes.updated.length + changes.removed.length;
    if (applied === 0) {
      return {
        eligible: false,
        blockers: ["Template field changes do not match any fields on this component"]
      };
    }
  }

  return {eligible: true, blockers: []};
};

/**
 * Flatten template.instances into rows for the propagate modal.
 *
 * Group/component metadata usually lives on the facility builder; the checksheet
 * builder holds the component copy to update. Rows are omitted when the component
 * is not present on the checksheet builder (stale instance registry entries).
 *
 * @param {object} template
 * @param {object} checksheetsLookup - map of checksheet id -> serialized checksheet
 * @param {{previousTemplate: object, newTemplate: object}} [templates] - when set, only rows that can receive the diff are returned
 * @returns {Array.<object>}
 */
export const flattenInstanceRows = (template, checksheetsLookup = {}, templates = {}) => {
  if (!template?.instances) return [];

  const {previousTemplate, newTemplate} = templates;

  return Object.entries(template.instances).flatMap(([checksheetId, componentNames]) =>
    (componentNames || [])
      .map(componentName => {
        const checksheet = checksheetsLookup[checksheetId];
        if (!checksheet) return null;

        const checksheetById = checksheet.builder?.byId || {};
        const facilityById = checksheet.facility?.builder?.byId || {};
        const checksheetComponent = checksheetById[componentName];

        if (!isChecksheetComponent(checksheetComponent)) return null;

        if (previousTemplate && newTemplate) {
          const {eligible} = assessPropagationEligibility(checksheet.builder, componentName, {
            previousTemplate,
            newTemplate,
            facilityById
          });
          if (!eligible) return null;
        }

        const group =
          (checksheetComponent.parentName && facilityById[checksheetComponent.parentName]) ||
          (checksheetComponent.parentName && checksheetById[checksheetComponent.parentName]) ||
          null;

        const componentLabel =
          checksheetComponent.label || facilityById[componentName]?.label || componentName;

        return {
          id: `${checksheetId}-${componentName}`,
          checksheetId,
          componentName,
          facilityName: checksheet.facility?.name,
          facilityType: checksheet.facility?.type,
          checksheetName: checksheet.name,
          frequencyName: checksheet.frequency?.name,
          groupLabel: group?.label,
          componentLabel,
          slug: checksheet.facility?.slug
        };
      })
      .filter(Boolean)
  );
};
